/**
 * InstructionsScreen v120.4 — Chat Dialogue Care Instructions
 * 
 * Replaces old green header with CompactHeader + chat dialogue:
 *   - Orella greeting bubble
 *   - SmallBubble → checklist cards with checkboxes
 *   - Progress bar
 *   - Completion bubble when all checked
 */

import { useState, useMemo, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { Check, Activity, ListChecks, Scissors, Syringe, FileText, ClipboardCheck, ChevronDown, Info, Lightbulb, Clock, ThumbsUp, AlertTriangle, MessageCircle, Eye } from 'lucide-react'
import { useCaseStore } from '../../stores'
import { aiAPI } from '../../services/api'
import { useI18n } from '../../i18n'
import { useAgent } from '../../components/AgentContext'
import { ChatKeyframes, CompactHeader, OrellaChatBubble, SmallOrellaBubble, AnimatedCard } from '../../components/OrellaChatHeader'
import { DmeItemCard, DmeSupplierSection } from './DmeCard'
import OrellaLoader from '../../components/OrellaLoader'
import { currentEncounterNumber } from '../../utils/caseEncounters'
import { caseHasSignedVisit, planFollowUp, signedOrdersPlaced } from '../../utils/signedPlan' // PROD-1079: no draft plan reaches the patient
import { keyItems, isDaily, dayKey, isTicked } from '../../utils/careTicks'
import { asText, asFirst } from '../../utils/asText' // PROD-1156: a case-plan value is drawn as text, never as an object

const NAVY = '#15354F'
const GREEN = '#66D170'
const GREEN_DARK = '#16A34A'

// Package icon — v221.1.2, used for DME (Equipment & Supplies).
// Defined inline to match the codebase's pattern for icons not in the pinned lucide-react version.
const Package = ({ size = 24, className = '', style = {} }) => (
  <svg className={className} style={style} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M16.5 9.4 7.55 4.24"/>
    <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
    <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
    <line x1="12" y1="22.08" x2="12" y2="12"/>
  </svg>
)

// ★ Q8 (TEST 2.07, folded in): the old strip `^[-•*\d.)\]]+\s*` put digits AND
//   "." in the SAME character class, so a dose at the start of the text —
//   "2.5 mg twice daily" — was itself a run of that class ("2", ".", "5") and
//   was eaten whole, leaving "mg twice daily". A real numbered marker ("1. ",
//   "2) ") is a SMALL integer followed by exactly one delimiter and then
//   whitespace; requiring that trailing `\s+` (rather than `\s*`) is what
//   tells "2." (a marker) apart from "2.5" (a dose) — the digit right after
//   the delimiter is exactly what a marker never has and a decimal always
//   does. A bare bullet character keeps the old `\s*` (zero or one space
//   after "•" was always stripped, and a bullet carries no digit ambiguity).
function parseInstructions(text) {
  if (!text) return []
  return text.split(/[\n;]/).map(l => l.trim()).filter(l => l.length > 0).map(l => l.replace(/^(?:[-•*]\s*|\d+[.)\]]\s+)/, '').trim()).filter(l => l.length > 0).map((text, idx) => ({ id: `instruction-${idx}`, text }))
}

const orderTypeConfigStructure = {
  careOrders: { titleKey: 'careInstructions', icon: Activity, color: '#15354F', completeKey: 'careOrdersComplete', checkedKey: 'careOrdersCheckedItems', educationKey: 'careOrdersEducation' },
  // v221.1.2: new canonical buckets
  diagnosticTests: { titleKey: 'diagnosticTests', icon: ListChecks, color: '#15354F', completeKey: 'diagnosticTestsComplete', checkedKey: 'diagnosticTestsCheckedItems' },
  dme: { titleKey: 'dme', icon: Package, color: '#15354F', completeKey: 'dmeComplete', checkedKey: 'dmeCheckedItems' },
  // Legacy back-compat
  additionalOrders: { titleKey: 'additionalInstructions', icon: ListChecks, color: '#64748B', completeKey: 'additionalOrdersComplete', checkedKey: 'additionalOrdersCheckedItems' },
  // v238.0.0: procedures = consent-level counseling primer (Sonnet); navy (purple dropped)
  procedures: { titleKey: 'procedures', icon: Syringe, color: '#15354F', completeKey: 'proceduresComplete', checkedKey: 'proceduresCheckedItems', counselingKey: 'proceduresCounseling' },
  customOrders: { titleKey: 'customOrders', icon: FileText, color: '#46A8F9', completeKey: 'customOrdersComplete', checkedKey: 'customOrdersCheckedItems' },
  // v221.1.2: referrals canonical Light Blue + Navy
  referrals: { titleKey: 'referrals', icon: ListChecks, color: '#46A8F9', completeKey: 'referralsComplete', checkedKey: 'referralsCheckedItems' },
  other: { titleKey: 'additionalInstructions', icon: ClipboardCheck, color: '#64748B', completeKey: 'otherComplete', checkedKey: 'otherCheckedItems' },
}

// v238.0.0 — Consent-level counseling card for the patient Procedures bucket.
// Reads as a pre-op primer (what / why / expect / benefits / risks / alternatives),
// acknowledged as "reviewed & understood" — NOT legal consent. Questions route
// to the AI agent; formal consent happens in person before the procedure.
function ProcedureCounselingCard({ proc, counsel, reviewed, isOpen, onToggleReview, onToggleOpen, onAskQuestion, ts }) {
  const NAVY = '#15354F'
  const BLUE = '#46A8F9'
  const GREEN = '#66D170'
  const sections = counsel ? [
    { key: 'what', icon: Info, label: ts('instructions.whatProcedureIs', 'What this procedure is'), text: counsel.what },
    { key: 'why', icon: Lightbulb, label: ts('instructions.whyRecommended', 'Why your doctor recommends it'), text: counsel.why },
    { key: 'expect', icon: Clock, label: ts('instructions.whatToExpect', 'What to expect'), text: counsel.expect },
    { key: 'benefits', icon: ThumbsUp, label: ts('instructions.expectedBenefits', 'Expected benefits'), text: counsel.benefits },
  ] : []

  return (
    <div className={`w-full rounded-[14px] overflow-hidden border-2 mb-3 transition-all ${reviewed ? 'border-green-200' : 'border-gray-200'} bg-white`}>
      <div className="h-[3px]" style={{ backgroundColor: NAVY }} />
      <div className="p-3.5">
        {counsel?.type && (
          <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wide mb-2 px-2 py-[3px] rounded-md"
            style={{ color: NAVY, background: '#EEF2F6' }}>
            <Scissors size={12} /> {counsel.type}
          </span>
        )}
        <p className="text-[14px] font-bold leading-snug" style={{ color: NAVY }}>{proc.text}</p>
        {counsel?.summary && <p className="text-[12.5px] font-medium leading-relaxed mt-1.5" style={{ color: '#64748B' }}>{counsel.summary}</p>}

        {counsel?.conditional && (
          <div className="flex gap-2 items-start mt-2.5 rounded-[9px] px-2.5 py-2" style={{ background: '#FFF8EC', border: '1px solid #FCE6BE' }}>
            <AlertTriangle size={13} style={{ color: '#D97706', flexShrink: 0, marginTop: 1 }} />
            <p className="text-[11px] font-semibold leading-snug" style={{ color: '#8A5A12' }}>
              <span className="font-extrabold">{ts('instructions.conditional', 'Conditional')}:</span> {counsel.conditional}
            </p>
          </div>
        )}

        <button onClick={onToggleOpen}
          className="inline-flex items-center gap-1.5 mt-3 rounded-[9px] px-3 py-2 cursor-pointer"
          style={{ color: NAVY, background: '#F6FAFD', border: '1.5px solid #D6E3EC', fontSize: 12.5, fontWeight: 700 }}>
          <Eye size={14} />
          {isOpen ? ts('instructions.hideDetails', 'Hide details') : ts('instructions.reviewProcedureDetails', 'Review procedure details')}
          <ChevronDown size={14} strokeWidth={2.5} style={{ transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
        </button>
      </div>

      <div className="overflow-hidden" style={{ transition: 'max-height .35s ease', maxHeight: isOpen ? 1600 : 0 }}>
        <div className="mx-3.5">
          {sections.filter(s => s.text).map((s, idx) => {
            const Icon = s.icon
            return (
              <div key={s.key} className="py-2.5" style={idx === 0 ? {} : { borderTop: '1px solid #EEF3F7' }}>
                <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide mb-1" style={{ color: BLUE }}>
                  <Icon size={13} /> {s.label}
                </p>
                <p className="text-[12.5px] leading-relaxed font-medium" style={{ color: '#475569' }}>{s.text}</p>
              </div>
            )
          })}
          {counsel?.risks?.length > 0 && (
            <div className="py-2.5" style={{ borderTop: '1px solid #EEF3F7' }}>
              <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide mb-1" style={{ color: '#C2410C' }}>
                <AlertTriangle size={13} /> {ts('instructions.risksConsider', 'Risks & things to consider')}
              </p>
              <ul className="list-disc pl-4 mt-1">
                {counsel.risks.map((r, ri) => (
                  <li key={ri} className="text-[12.5px] leading-relaxed font-medium mb-0.5" style={{ color: '#475569' }}>{r}</li>
                ))}
              </ul>
            </div>
          )}
          {counsel?.alternatives && (
            <div className="py-2.5" style={{ borderTop: '1px solid #EEF3F7' }}>
              <p className="flex items-center gap-1.5 text-[11px] font-extrabold uppercase tracking-wide mb-1" style={{ color: BLUE }}>
                <ListChecks size={13} /> {ts('instructions.alternatives', 'Alternatives')}
              </p>
              <p className="text-[12.5px] leading-relaxed font-medium" style={{ color: '#475569' }}>{counsel.alternatives}</p>
            </div>
          )}
        </div>

        <button onClick={onAskQuestion}
          className="inline-flex items-center gap-1.5 mt-1 ml-3.5 bg-transparent p-0 cursor-pointer"
          style={{ color: BLUE, fontSize: 12.5, fontWeight: 700, border: 'none' }}>
          <MessageCircle size={14} /> {ts('instructions.haveQuestion', 'I have a question about this')}
        </button>

        <div className="mx-3.5 my-3.5 rounded-[12px] p-3" style={{ background: '#F8FAFC', border: '1px solid #E6EDF3' }}>
          <button onClick={onToggleReview} className="flex gap-3 items-start w-full text-left bg-transparent border-none p-0 cursor-pointer">
            <span className="w-6 h-6 rounded-[7px] flex items-center justify-center flex-shrink-0 mt-0.5 transition-all"
              style={reviewed ? { backgroundColor: GREEN, border: 'none' } : { background: 'transparent', border: '2px solid #CBD5E1' }}>
              {reviewed && <Check size={14} className="text-white" strokeWidth={2.5} />}
            </span>
            <span className="text-[12.5px] font-semibold leading-snug" style={{ color: NAVY }}>
              {ts('instructions.reviewedUnderstand', 'I\u2019ve reviewed and understand this information.')}
            </span>
          </button>
          <p className="text-[11px] font-medium mt-2 leading-snug" style={{ color: '#94A3B8', paddingLeft: 35 }}>
            {ts('instructions.ackNotConsent', 'This confirms you\u2019ve read the summary. It is not your formal surgical consent \u2014 your surgeon completes that with you in person.')}
          </p>
        </div>
      </div>
    </div>
  )
}

export default function InstructionsScreen() {
  const navigate = useNavigate()
  const { openAgent } = useAgent()
  const location = useLocation()
  const { currentCase, updateDeliverTask } = useCaseStore()
  const { t } = useI18n()
  const ts = (key, fallback) => { const v = t(key); return (!v || v === key) ? fallback : v }

  const orderTypeConfig = useMemo(() => {
    const result = {}
    Object.entries(orderTypeConfigStructure).forEach(([k, v]) => {
      result[k] = { ...v, title: t('instructions.' + v.titleKey) }
    })
    return result
  }, [t])

  const orderType = location.state?.orderType || 'other'
  const passedItems = location.state?.items || []
  const passedText = location.state?.text || ''
  const config = orderTypeConfig[orderType] || orderTypeConfig.other

  const [step, setStep] = useState(0)
  const [checkedItems, setCheckedItems] = useState(new Set())
  const [isSubmitting, setIsSubmitting] = useState(false)
  // v237.0.0: "Learn more" plain-language education
  const [expandedItems, setExpandedItems] = useState(new Set())
  const [education, setEducation] = useState(null)
  const [eduLoading, setEduLoading] = useState(false)
  // v238.0.0: procedure consent-level counseling
  const [counseling, setCounseling] = useState(null)
  const [counselLoading, setCounselLoading] = useState(false)
  // v237.4.0: DME equipment education
  const [dmeEdu, setDmeEdu] = useState(null)
  const [dmeEduLoading, setDmeEduLoading] = useState(false)
  // v239.5.0: hold the branded loader until this order type's AI content settles
  const [contentReady, setContentReady] = useState(false)
  const prepAttemptedRef = useRef(false)

  const caseInfo = `${(currentCase?.specialtyName || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} > ${currentCase?.focusAreaName || ''}`

  // ★ PROD-1079: no signed visit -> back to Case Home (DeliverScreen's backstop, same rule).
  //   Before signing, cases.follow_up is md's draft plan (utils/signedPlan.js).
  useEffect(() => {
    if (currentCase && !caseHasSignedVisit(currentCase)) navigate('/case/home', { replace: true })
  }, [currentCase, navigate])

  const instructionsData = useMemo(() => {
    if (!currentCase) return { items: [], checkedItems: [] }
    const followUp = planFollowUp(currentCase) // PROD-1079: plan keys only once signed
    const ordersPlaced = signedOrdersPlaced(currentCase)
    const deliver = currentCase.deliver || {}
    const requirements = followUp.requirements || {}
    let items = []

    if (orderType === 'other') {
      const rawText = asFirst(passedText, requirements.otherText, deliver.otherRequirements, currentCase.otherRequirements, currentCase.diagnose?.otherRequirements) // PROD-1156
      items = parseInstructions(rawText)
    } else {
      const orderItems = passedItems.length > 0 ? passedItems : ordersPlaced[orderType] || []
      items = orderItems.map((item, idx) => ({ id: `${orderType}-${idx}`, text: typeof item === 'string' ? item : asFirst(item?.name, item?.device, item?.description, item?.test, item), raw: item })) // PROD-1156: never JSON.stringify
    }

    // ★ TEST 2.14: content-addressed keys, ALONGSIDE the existing position
    //   `id` (kept for anything still reading it, and as the React list key).
    return { items: keyItems(orderType, items) }
  }, [currentCase, orderType, passedItems, passedText])

  // ★ TEST 2.14: ticks now live per-encounter, content-addressed, in
  //   `deliver.careTicks` — not the flat position-id `<type>CheckedItems`
  //   array this effect used to seed `checkedItems` from. A NEW encounter's
  //   orders (even ones with wording identical to a past encounter's) start
  //   unticked, because `careTicks` is keyed first by `enc`.
  const enc = `e${currentEncounterNumber(currentCase)}`
  const today = dayKey()
  const daily = isDaily(orderType)
  useEffect(() => {
    const store = currentCase?.deliver?.careTicks
    const ticked = new Set()
    for (const item of instructionsData.items) {
      if (isTicked(store, enc, item.key, daily, today)) ticked.add(item.key)
    }
    setCheckedItems(ticked)
  }, [instructionsData.items, currentCase?.deliver?.careTicks, enc, daily, today])

  // v237.0.0: Load cached "Learn more" education, or generate once via Haiku.
  // Gated on config.educationKey so only care-order-style buckets get it.
  useEffect(() => {
    if (!config.educationKey || !currentCase?.id) return
    const items = instructionsData.items
    if (!items || items.length === 0) return

    const cached = currentCase.deliver?.[config.educationKey]
    if (cached?.education?.length >= items.length) {
      setEducation(cached)
      return
    }
    if (eduLoading) return

    let cancelled = false
    setEduLoading(true)
    ;(async () => {
      try {
        const res = await aiAPI.generateOrderEducation(currentCase.id, orderType, items.map(i => i.text))
        if (cancelled) return
        if (res?.success && res.education?.education?.length) {
          setEducation(res.education)
          updateDeliverTask(orderType, { [config.educationKey]: res.education })
        }
      } catch (err) {
        console.warn('[Instructions] education error:', err)
      } finally {
        if (!cancelled) setEduLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [config.educationKey, currentCase?.id, orderType, instructionsData.items])

  const toggleExpand = (id) => {
    setExpandedItems(prev => {
      const n = new Set(prev)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }

  // v238.0.0: Procedures — generate consent-level counseling primer once (Sonnet).
  useEffect(() => {
    if (!config.counselingKey || !currentCase?.id) return
    const items = instructionsData.items
    if (!items || items.length === 0) return

    const cached = currentCase.deliver?.[config.counselingKey]
    if (cached?.counseling?.length >= items.length) {
      setCounseling(cached)
      return
    }
    if (counselLoading) return

    let cancelled = false
    setCounselLoading(true)
    ;(async () => {
      try {
        const res = await aiAPI.generateProcedureCounseling(currentCase.id, items.map(i => i.text))
        if (cancelled) return
        if (res?.success && res.counseling?.counseling?.length) {
          setCounseling(res.counseling)
          updateDeliverTask(orderType, { [config.counselingKey]: res.counseling })
        }
      } catch (err) {
        console.warn('[Instructions] counseling error:', err)
      } finally {
        if (!cancelled) setCounselLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [config.counselingKey, currentCase?.id, orderType, instructionsData.items])

  const askProcedureQuestion = (procText) => {
    const seed = ts('instructions.procedureQuestionSeed', 'I have a question about my recommended procedure: ') + procText
    openAgent(seed, 'procedure_counseling')
  }

  // v237.4.0: DME — generate equipment education once (Haiku, cached)
  useEffect(() => {
    if (orderType !== 'dme' || !currentCase?.id) return
    const items = instructionsData.items
    if (!items || items.length === 0) return
    const cached = currentCase.deliver?.dmeEducation
    if (cached?.education?.length >= items.length) { setDmeEdu(cached); return }
    if (dmeEduLoading) return
    let cancelled = false
    setDmeEduLoading(true)
    ;(async () => {
      try {
        const res = await aiAPI.generateDmeEducation(currentCase.id, items.map(i => i.text))
        if (cancelled) return
        if (res?.success && res.education?.education?.length) {
          setDmeEdu(res.education)
          updateDeliverTask('dme', { dmeEducation: res.education })
        }
      } catch (err) {
        console.warn('[Instructions] dme education error:', err)
      } finally {
        if (!cancelled) setDmeEduLoading(false)
      }
    })()
    return () => { cancelled = true }
  }, [orderType, currentCase?.id, instructionsData.items])

  const totalItems = instructionsData.items.length
  const completedItems = checkedItems.size
  const allChecked = completedItems === totalItems && totalItems > 0
  const isProcedures = orderType === 'procedures'
  const isDme = orderType === 'dme'

  // v239.5.0 — Hold the canonical OrellaLoader until the AI-generated content for
  // THIS order type is ready (or has finished generating, even on error), then
  // reveal the page. Replaces the inline grey "Preparing…" line: the page now
  // opens only once content is ready, with the branded spinner shown meanwhile.
  const usesAIContent = isProcedures || isDme || !!config.educationKey
  const aiContentReady = isProcedures ? !!counseling : isDme ? !!dmeEdu : !!education
  const aiContentLoading = isProcedures ? counselLoading : isDme ? dmeEduLoading : (config.educationKey ? eduLoading : false)
  useEffect(() => {
    // No items, or an order type with no AI content → reveal immediately.
    if (!currentCase?.id || instructionsData.items.length === 0 || !usesAIContent) {
      setContentReady(true)
      return
    }
    if (aiContentLoading) prepAttemptedRef.current = true
    // Reveal once content arrives, OR once a generation attempt has finished
    // (success or error) so the user is never stuck behind the spinner.
    if (aiContentReady || (prepAttemptedRef.current && !aiContentLoading)) setContentReady(true)
  }, [currentCase?.id, instructionsData.items.length, usesAIContent, aiContentReady, aiContentLoading])

  const dmeRecommended = isDme
    ? (signedOrdersPlaced(currentCase).dmeFacility
       || (() => { const r = instructionsData.items.find(it => it.raw?.supplier); return r ? { name: r.raw.supplier } : null })())
    : null

  const handleToggle = (id) => {
    setCheckedItems(prev => {
      const n = new Set(prev)
      n.has(id) ? n.delete(id) : n.add(id)
      return n
    })
  }

  const handleSave = async () => {
    if (isSubmitting) return
    setIsSubmitting(true)
    try {
      // ★ TEST 2.14: write every item's tick state (not just the ones that
      //   changed) so this Save is a complete, self-consistent snapshot of
      //   `careTicks[enc]` for this order type — no diffing against a store
      //   read that could already be stale.
      const ticksForEnc = {}
      for (const item of instructionsData.items) {
        ticksForEnc[item.key] = daily ? { days: { [today]: checkedItems.has(item.key) } } : { once: checkedItems.has(item.key) }
      }
      // Legacy flat array (position ids), for the pre-visit report and any
      // reader not yet on careTicks — derived from THIS save's own state, not
      // re-read from the store, so the two can never disagree.
      const legacyChecked = instructionsData.items
        .filter((item) => checkedItems.has(item.key))
        .map((item) => item.id)
      await updateDeliverTask(orderType, {
        careTicks: { [enc]: ticksForEnc },
        [config.completeKey]: checkedItems.size === totalItems,
        [config.checkedKey]: legacyChecked,
        [`${orderType}SavedAt`]: new Date().toISOString()
      })
      navigate('/case/deliver')
    } catch (err) { console.error('Failed:', err) }
    finally { setIsSubmitting(false) }
  }

  // v239.5.0 — Branded loading gate. While the AI summary for this order type is
  // being prepared, show the canonical OrellaLoader (Orella O + blue ring) instead
  // of rendering the page with empty cards behind a grey "Preparing…" line.
  if (!contentReady) {
    const prepMessage = isProcedures
      ? ts('instructions.preparingCounseling', 'Preparing your procedure summary…')
      : isDme
        ? ts('instructions.preparingEquipment', 'Preparing your equipment details…')
        : ts('instructions.preparingDetails', 'Preparing plain-language details…')
    return (
      <div className="min-h-full flex flex-col bg-[#F8FAFC]">
        <ChatKeyframes />
        <CompactHeader stepNum={4} totalSteps={4} stepLabel="Deliver" caseInfo={caseInfo} statusLabel="Active" onBack={() => navigate('/case/deliver')} />
        <OrellaLoader message={prepMessage} />
      </div>
    )
  }

  return (
    <div className="min-h-full flex flex-col bg-[#F8FAFC]">
      <ChatKeyframes />
      <CompactHeader stepNum={4} totalSteps={4} stepLabel="Deliver" caseInfo={caseInfo} statusLabel="Active" onBack={() => navigate('/case/deliver')} />

      {/* Progress */}
      <div className="px-4 py-2.5 bg-white border-b border-gray-100">
        <div className="flex justify-between mb-1">
          <span className="text-[11px] font-semibold text-gray-500">{config.title}</span>
          <span className="text-[11px] font-semibold" style={{ color: GREEN_DARK }}>{completedItems}/{totalItems} {(isProcedures || isDme) ? ts('instructions.reviewed', 'reviewed') : ts('instructions.done', 'done')}</span>
        </div>
        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${totalItems > 0 ? (completedItems / totalItems) * 100 : 0}%`, backgroundColor: GREEN }} />
        </div>
      </div>

      <div className="flex-1 overflow-auto pb-6">
        <OrellaChatBubble
          message={isProcedures
            ? ts('instructions.proceduresIntro', 'Your doctor has recommended the procedure(s) below. Review the details, then confirm you understand.')
            : isDme
              ? ts('instructions.dmeIntro', 'Your doctor ordered equipment for you. Learn how to use it, find where to get it, and mark it reviewed.')
              : orderType === 'careOrders'
                ? ts('instructions.careOrdersIntro', 'Your doctor has care instructions for you. Tap Learn more to understand each one, then check it off when done.')
                : ts('instructions.doctorHasOrders', 'Your doctor has care instructions for you. Review and check each one off as you complete.')}
          animationKey={`instructions-${currentCase?.id}-${orderType}`}
          onComplete={() => setStep(1)} />

        {/* v238.0.0: procedures consent-level primer banner */}
        {isProcedures && (
          <div className="mx-4 mb-3 flex gap-2 items-start rounded-[12px] px-3 py-2.5" style={{ background: '#FFF8EC', border: '1px solid #FCE6BE' }}>
            <Info size={15} style={{ color: '#D97706', flexShrink: 0, marginTop: 1 }} />
            <p className="text-[11.5px] font-semibold leading-snug" style={{ color: '#8A5A12' }}>
              {ts('instructions.consentDisclaimer', 'This is an educational summary to help you prepare. Your surgeon will review the full details and complete formal consent with you in person before anything is done.')}
            </p>
          </div>
        )}

        {/* v239.5.0: inline grey "Preparing…" removed — page now gates on OrellaLoader until content is ready */}

        {isProcedures && instructionsData.items.map((inst, i) => (
          <AnimatedCard key={inst.id} visible={step >= 1} delay={300 + i * 120}>
            <ProcedureCounselingCard
              proc={inst}
              counsel={counseling?.counseling?.[i]}
              reviewed={checkedItems.has(inst.key)}
              isOpen={expandedItems.has(inst.id)}
              onToggleReview={() => handleToggle(inst.key)}
              onToggleOpen={() => toggleExpand(inst.id)}
              onAskQuestion={() => askProcedureQuestion(inst.text)}
              ts={ts}
            />
          </AnimatedCard>
        ))}

        {isDme && (<>
          {instructionsData.items.map((inst, i) => (
            <AnimatedCard key={inst.id} visible={step >= 1} delay={300 + i * 120}>
              <DmeItemCard
                item={inst.raw || inst.text}
                edu={dmeEdu?.education?.[i]}
                reviewed={checkedItems.has(inst.key)}
                isOpen={expandedItems.has(inst.id)}
                onToggleReview={() => handleToggle(inst.key)}
                onToggleOpen={() => toggleExpand(inst.id)}
                ts={ts}
              />
            </AnimatedCard>
          ))}
          <AnimatedCard visible={step >= 1} delay={300 + instructionsData.items.length * 120}>
            <DmeSupplierSection recommended={dmeRecommended} currentCase={currentCase} updateDeliverTask={updateDeliverTask} ts={ts} />
          </AnimatedCard>
        </>)}

        {!isProcedures && !isDme && instructionsData.items.map((inst, i) => {
          const isDone = checkedItems.has(inst.key)
          const edu = education?.education?.[i]
          const hasEdu = !!(edu && (edu.what || edu.why || edu.how))
          const isOpen = expandedItems.has(inst.id)
          return (
            <AnimatedCard key={inst.id} visible={step >= 1} delay={300 + i * 120}>
              <div className={`w-full rounded-[14px] overflow-hidden border-2 mb-2.5 transition-all ${
                isDone ? 'border-green-200 bg-gradient-to-b from-green-50/50 to-white' : 'border-gray-200 bg-white'
              }`}>
                <div className="h-[3px]" style={{ backgroundColor: isDone ? GREEN : '#E8F4FE', opacity: isDone ? 1 : 0.5 }} />
                <div className="p-3.5 flex items-start gap-3">
                  {/* Check-to-complete — v237.0.0: check mark is the only completion signal (no strikethrough) */}
                  <button onClick={() => handleToggle(inst.key)} aria-label={ts('instructions.markComplete', 'Mark complete')}
                    className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-all cursor-pointer"
                    style={isDone
                      ? { backgroundColor: GREEN, border: 'none' }
                      : { background: 'transparent', border: '2px solid #CBD5E1' }}>
                    {isDone && <Check size={14} className="text-white" strokeWidth={2.5} />}
                  </button>
                  <div className="flex-1 min-w-0">
                    <p className="text-[13px] font-semibold leading-snug" style={{ color: NAVY }}>{inst.text}</p>
                    {hasEdu && (
                      <button onClick={() => toggleExpand(inst.id)}
                        className="inline-flex items-center gap-1.5 mt-2 bg-transparent p-0 cursor-pointer"
                        style={{ color: '#46A8F9', fontSize: 12.5, fontWeight: 700, border: 'none' }}>
                        {ts('instructions.learnMore', 'Learn more')}
                        <ChevronDown size={14} strokeWidth={2.5} style={{ transition: 'transform .2s', transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                      </button>
                    )}
                  </div>
                </div>
                {hasEdu && (
                  <div className="overflow-hidden" style={{ transition: 'max-height .3s ease', maxHeight: isOpen ? 400 : 0 }}>
                    <div className="mx-3.5 mb-3.5 rounded-[11px] p-3.5" style={{ background: '#F6FAFD', border: '1px solid #E4EFF7' }}>
                      {edu.what && (<>
                        <p className="text-[10.5px] font-extrabold uppercase tracking-wide mb-1" style={{ color: '#46A8F9' }}>{ts('instructions.whatThisMeans', 'What this means')}</p>
                        <p className="text-[12.5px] leading-relaxed font-medium mb-2.5" style={{ color: '#475569' }}>{edu.what}</p>
                      </>)}
                      {edu.why && (<>
                        <p className="text-[10.5px] font-extrabold uppercase tracking-wide mb-1" style={{ color: '#46A8F9' }}>{ts('instructions.whyItMatters', 'Why it matters')}</p>
                        <p className="text-[12.5px] leading-relaxed font-medium mb-2.5" style={{ color: '#475569' }}>{edu.why}</p>
                      </>)}
                      {edu.how && (<>
                        <p className="text-[10.5px] font-extrabold uppercase tracking-wide mb-1" style={{ color: '#46A8F9' }}>{ts('instructions.howToDoIt', 'How to do it')}</p>
                        <p className="text-[12.5px] leading-relaxed font-medium" style={{ color: '#475569' }}>{edu.how}</p>
                      </>)}
                    </div>
                  </div>
                )}
              </div>
            </AnimatedCard>
          )
        })}
      </div>

      <div className="p-4 pb-6 bg-white border-t border-gray-100 flex-shrink-0">
        <button onClick={handleSave} disabled={isSubmitting}
          className="w-full py-3.5 rounded-[14px] text-[14px] font-bold flex items-center justify-center gap-2"
          style={{ background: `linear-gradient(135deg, ${NAVY}, #1E4A6E)`, color: '#fff' }}>
          <Check size={16} /> {isSubmitting ? t('instructions.saving') : t('instructions.saveProgress', { completed: completedItems, total: totalItems })}
        </button>
      </div>
    </div>
  )
}
